import { Link } from "react-router-dom";
import { Navbar } from "@/components/landing/Navbar";

export const Privacy = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="container px-4 pt-32 pb-16 max-w-4xl">
        <div className="prose prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
          
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">Last Updated: January 6, 2026</h2>
              <p className="text-muted-foreground">
                Sharpexio ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our cryptocurrency portfolio analytics platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">1.1 Wallet Information</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Public wallet addresses (read-only)</li>
                <li>Blockchain transaction data (publicly available)</li>
                <li>Portfolio and trading history from connected exchanges</li>
                <li>Connection timestamps and usage patterns</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">1.2 Technical Information</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on our platform</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">1.3 Account Information</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Email address (when provided)</li>
                <li>User preferences and settings</li>
                <li>Subscription and payment information (if applicable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>To provide and maintain our analytics service</li>
                <li>To fetch and analyze blockchain and exchange data</li>
                <li>To personalize your experience and display relevant analytics</li>
                <li>To improve our platform and develop new features</li>
                <li>To communicate with you about service updates</li>
                <li>To ensure platform security and prevent fraud</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Information We Do Not Collect</h2>
              
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-primary mb-3">🔒 What We Never Collect</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li><strong>Private Keys:</strong> We never request, access, or store private keys or seed phrases</li>
                  <li><strong>Passwords:</strong> We do not collect or store wallet passwords</li>
                  <li><strong>Trading Permissions:</strong> We only use read-only access to your accounts</li>
                  <li><strong>Personal Financial Information:</strong> We don't collect bank details or credit card information</li>
                  <li><strong>Identity Documents:</strong> We do not collect KYC documents unless explicitly provided</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
              <p className="text-muted-foreground">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Encryption of data in transit using HTTPS/TLS</li>
                <li>Secure storage of sensitive data</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication systems</li>
                <li>Secure wallet connections using WalletConnect protocols</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                However, no method of transmission over the internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground">
                We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations or court orders</li>
                <li>To protect our rights, property, or safety</li>
                <li>To trusted service providers who assist in operating our platform</li>
                <li>In connection with a business merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Blockchain Data and Public Information</h2>
              <p className="text-muted-foreground">
                Please note that blockchain transactions and wallet addresses are inherently public. Information that you make public on blockchain networks may be collected and used by others. We are not responsible for the privacy practices of blockchain networks or third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-muted-foreground">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how our platform is used</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You can control cookies through your browser settings, but disabling certain cookies may affect platform functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Your Rights and Choices</h2>
              <p className="text-muted-foreground">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Restriction:</strong> Limit how we use your personal information</li>
                <li><strong>Objection:</strong> Object to certain uses of your personal information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal information only as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete your personal information unless we are required to retain it for legal, security, or legitimate business purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
              <p className="text-muted-foreground">
                Sharpexio may transfer and process your personal information in countries other than your own. When we do so, we ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our service constitutes acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Contact Information</h2>
              <p className="text-muted-foreground">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-muted/50 rounded-lg p-4 mt-4">
                <p>Email: privacy@sharpex.io</p>
                <p>Website: https://sharpex.io</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Governing Law</h2>
              <p className="text-muted-foreground">
                This Privacy Policy shall be governed by the laws of the jurisdiction in which Sharpexio operates, without regard to conflict of law provisions.
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
